import React from "react";
import { ButtonShadcn } from "@/components/ui/Button";
import { cn } from "@/lib/utils";

/**
 * VerticalSectionNav
 * Presentational sidebar listing section labels; highlights the active one
 * and calls onSelect when clicked. Pair with the `useScrollSpy` hook for
 * scroll-driven active state + click-to-scroll behavior, or use it as a
 * plain vertical tab list (pass `onSelect` only, ignore scroll-spy).
 *
 * @param {{id: string, label: string, count?: number, disabled?: boolean}[]} sections
 * @param {string} activeSection
 * @param {(id: string) => void} onSelect
 * @param {string} [className] - extra classes for the <nav> wrapper.
 */
export const VerticalSectionNav = ({ sections, activeSection, onSelect, className = "" }) => (
  <nav className={cn("flex min-w-[180px] max-w-[200px] shrink-0 flex-col gap-1 pr-2", className)}>
    {sections.map((section) => {
      const isActive = section.id === activeSection;
      const isDisabled = !!section.disabled;
      return (
        <ButtonShadcn
          variant="ghost"
          key={section.id}
          onClick={() => !isDisabled && onSelect(section.id)}
          disabled={isDisabled}
          className={cn(
            "h-auto justify-start flex items-center gap-2 text-left rounded-lg px-1 py-2 text-sm transition-colors",
            isDisabled
              ? "text-text-muted cursor-not-allowed opacity-60"
              : isActive
              ? "font-medium text-text-primary scale-105 ml-1"
              : "text-text-secondary hover:bg-background-tertiary border-l-2 border-transparent"
          )}
        >
          <span>{section.label}</span>
          {typeof section.count === "number" && section.count > 0 && (
            <span
              className={cn(
                "shrink-0 text-[8px] font-semibold px-1.5 rounded-full border",
                isActive ? "bg-background-invert text-text-invert border-transparent" : "border-border-primary text-text-secondary"
              )}
            >
              {String(section.count).padStart(2, "0")}
            </span>
          )}
        </ButtonShadcn>
      );
    })}
  </nav>
);

export default VerticalSectionNav;